// Central, SSR-safe access point for Bugsnag. The browser notifiers are booted
// in `instrumentation-client.ts` (client-only, before hydration) and registered
// here via `registerClient` / `registerErrorBoundary`. Every function below is a
// guarded no-op until then, so this module is safe to import from code that also
// runs during SSR / on the Cloudflare Worker (the auth store, the API client).

import type { BreadcrumbType, Client, Event } from "@bugsnag/js"
import type { ComponentType, ReactNode } from "react"

// --- config (read from build-time env; inlined by Next) ---

export const API_KEY = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY ?? ""
export const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? "dev"
export const RELEASE_STAGE =
  process.env.NEXT_PUBLIC_RELEASE_STAGE ?? "development"

// Local/dev builds never report — only these stages send events (errors + perf).
export const ENABLED_RELEASE_STAGES = ["production", "staging"]

// Keys whose values are stripped before an event leaves the device. Covers auth
// material and member PII — we only ever attach `member.id`, but this is a
// belt-and-suspenders guard for anything that rides along on metadata.
export const REDACTED_KEYS: (string | RegExp)[] = [
  /token/i,
  /secret/i,
  /password/i,
  /authorization/i,
  "cardNumber",
  "card_number",
  "phone",
  "email",
  "dob",
  "fullName",
  "photoUrl",
]

// --- runtime registry (populated by instrumentation-client) ---

export type BugsnagErrorBoundary = ComponentType<{
  FallbackComponent?: ComponentType<{
    error: Error
    info: { componentStack: string }
    clearError: () => void
  }>
  onError?: (event: Event) => void
  children?: ReactNode
}>

let client: Client | null = null
let errorBoundary: BugsnagErrorBoundary | null = null

export function registerClient(c: Client) {
  client = c
}

export function registerErrorBoundary(boundary: BugsnagErrorBoundary) {
  errorBoundary = boundary
}

/** The plugin-react ErrorBoundary, or null until Bugsnag has started (SSR). */
export function getErrorBoundary(): BugsnagErrorBoundary | null {
  return errorBoundary
}

// --- guarded API (no-op until started) ---

/** Report a handled error. Silently ignored until Bugsnag has started. */
export function notify(error: Error, onError?: (event: Event) => void) {
  client?.notify(error, onError)
}

export function leaveBreadcrumb(
  message: string,
  metadata?: Record<string, unknown>,
  type?: BreadcrumbType
) {
  client?.leaveBreadcrumb(message, metadata, type)
}

/** Tie subsequent events to a member — ID only, never contact PII. */
export function identifyMember(id: string) {
  client?.setUser(id)
}

export function clearUser() {
  client?.setUser(undefined)
}
